/**
 * sectionVisibility — Centralized configuration for section/widget visibility by interface mode.
 *
 * Each section defines:
 * - minMode: minimum interface mode to display
 * - intentOverride: optional overrides that promote a section to a lower mode based on user intent
 * - depthVariant: optional depth variation per mode (e.g. "compact" vs "full")
 *
 * @since Progressive Interface System
 */

import type { InterfaceMode } from "@/hooks/useInterfaceMode";
import type { UserIntent } from "@/hooks/useUserIntentProfile";
import type { SimpleGoal } from "@/hooks/useUserIntentProfile";

export interface IntentOverride {
  intent: UserIntent;
  promoteToMode: InterfaceMode;
}

export interface SectionConfig {
  minMode: InterfaceMode;
  intentOverride?: IntentOverride[];
  depthVariant?: Partial<Record<InterfaceMode, string>>;
  /** If set, section is only visible in simple mode for these goal presets */
  simpleGoalFilter?: SimpleGoal[];
}

const MODE_ORDER: Record<InterfaceMode, number> = {
  simple: 0,
  core: 1,
  advanced: 2,
};

// ── Section registry ──────────────────────────────────

export const sectionVisibility: Record<string, SectionConfig> = {
  // ── Dashboard ──
  "dashboard.today-card": { minMode: "simple" },
  "dashboard.next-step": { minMode: "simple" },
  "dashboard.mode-indicator": { minMode: "simple" },
  "dashboard.personalize-card": { minMode: "simple" }, // simple only — hidden in core/advanced
  "dashboard.onboarding-cta": { minMode: "simple" },
  "dashboard.journal": {
    minMode: "simple",
    depthVariant: { simple: "compact", core: "full", advanced: "full" },
  },
  "dashboard.weekly-summary": { minMode: "core" },
  "dashboard.progression-cards": { minMode: "core" },
  "dashboard.module-suggestions": { minMode: "core" },
  "dashboard.whats-new": { minMode: "core" },
  "dashboard.mini-radar": { minMode: "advanced" },

  // ── Programs page tabs (simple sub-preset gating) ──
  "programs.tab.planning": {
    minMode: "core",
    intentOverride: [
      { intent: "structured_program", promoteToMode: "simple" },
      { intent: "follow_program", promoteToMode: "simple" },
    ],
    simpleGoalFilter: ["follow_program"],
  },
  "programs.tab.my-programs": {
    minMode: "simple",
    simpleGoalFilter: ["follow_program", "explore"],
  },
  "programs.tab.sessions": {
    minMode: "simple",
    simpleGoalFilter: ["create_session", "explore"],
  },
  "programs.tab.generate-program": {
    minMode: "simple",
    simpleGoalFilter: ["follow_program", "explore"],
  },
  "programs.tab.generate-session": {
    minMode: "simple",
    simpleGoalFilter: ["create_session", "explore"],
  },

  // ── Programs (legacy keys kept for non-tab gating) ──
  "programs.active-program": { minMode: "simple" },
  "programs.create": { minMode: "simple" },
  "programs.generate": { minMode: "simple" },
  "programs.full-list": { minMode: "core" },
  "programs.analysis-summary": { minMode: "core" },
  "programs.obsolescence": { minMode: "core" },
  "programs.optimization": { minMode: "core" },
  "programs.manual-builder": { minMode: "advanced" },
  "programs.deep-quality": { minMode: "advanced" },

  // ── Profile ──
  "profile.identity": { minMode: "simple" },
  "profile.sport-level": { minMode: "simple" },
  "profile.edit-answers": { minMode: "simple" },
  "profile.radar": { minMode: "core" },
  "profile.sports-history": { minMode: "core" },
  "profile.progression": { minMode: "core" },
  "profile.comparison": { minMode: "advanced" },
  "profile.timeline": { minMode: "advanced" },
  "profile.advanced-analysis": { minMode: "advanced" },
  "profile.analyse-tab": { minMode: "core" },

  // ── Library ──
  "sidebar.library": {
    minMode: "advanced",
    intentOverride: [
      { intent: "understand_training", promoteToMode: "core" },
      { intent: "learn", promoteToMode: "core" },
    ],
  },

  // ── Community ──
  "sidebar.community": { minMode: "core" },
  "community.feed": { minMode: "core" },
  "community.sharing": { minMode: "core" },
  "community.discussions": { minMode: "advanced" },

  // ── Planning ──
  "planning.weekly-grid": {
    minMode: "core",
    intentOverride: [
      { intent: "structured_program", promoteToMode: "simple" },
      { intent: "follow_program", promoteToMode: "simple" },
    ],
  },
  "planning.cycle-config": { minMode: "advanced" },
  "planning.multi-week": { minMode: "advanced" },

  // ── Sidebar ──
  "sidebar.programs": {
    minMode: "simple",
    simpleGoalFilter: ["follow_program", "explore"],
  },
  "sidebar.sessions": {
    minMode: "simple",
    simpleGoalFilter: ["create_session", "explore"],
  },
  "sidebar.planning": {
    minMode: "core",
    intentOverride: [
      { intent: "structured_program", promoteToMode: "simple" },
      { intent: "explore", promoteToMode: "simple" },
      { intent: "explore_platform", promoteToMode: "simple" },
      { intent: "track_progress", promoteToMode: "simple" },
    ],
    simpleGoalFilter: ["explore", "track_progress"],
  },
  "sidebar.listening": { minMode: "advanced" },
  "sidebar.modules-panel": { minMode: "advanced" },
};

// ── Helpers ──────────────────────────────────────────

/**
 * Check if a section should be visible given the current mode and optional user intent.
 */
export function isSectionVisible(
  sectionId: string,
  currentMode: InterfaceMode,
  userIntent?: UserIntent | null,
  primaryGoal?: SimpleGoal | null
): boolean {
  const config = sectionVisibility[sectionId];
  if (!config) return true; // Unknown section → visible by default

  let effectiveMinMode = config.minMode;

  // Check intent overrides
  if (userIntent && config.intentOverride) {
    for (const override of config.intentOverride) {
      if (override.intent === userIntent) {
        if (MODE_ORDER[override.promoteToMode] < MODE_ORDER[effectiveMinMode]) {
          effectiveMinMode = override.promoteToMode;
        }
      }
    }
  }

  const modeOk = MODE_ORDER[currentMode] >= MODE_ORDER[effectiveMinMode];
  if (!modeOk) return false;

  // In simple mode, apply goal-based sub-preset filtering
  if (currentMode === "simple" && config.simpleGoalFilter && primaryGoal) {
    return config.simpleGoalFilter.includes(primaryGoal);
  }

  return true;
}

/**
 * Get the depth variant for a section given the current mode.
 */
export function getSectionDepth(
  sectionId: string,
  currentMode: InterfaceMode
): string | undefined {
  const config = sectionVisibility[sectionId];
  if (!config?.depthVariant) return undefined;
  return config.depthVariant[currentMode];
}
